import React, { useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';

import { VENDORS_SELECT } from 'app/src/components/Vendors/queries';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
} from 'app/src/elements';
import { Button } from 'app/src/elements/buttons';
import Modal from 'app/src/elements/Modal';
import { Text } from 'app/src/styles';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';

import NewVendor from 'app/src/components/Vendors/new';
import EditVendor from 'app/src/components/Vendors/edit';

import SubNav from './SubNav';
import EditExpense from './edit';

import { TXNS_WITH_COUNT, CONNECT_TXNS_TO_ENTITY } from '../Txns/queries';

const VendorIssues = () => {
  const [refetch, setRefetch] = useState();
  const [selected, setSelected] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [showEditModal, setShowEditModal] = useState();
  const [showAddVendorModal, setShowAddVendorModal] = useState();
  const [showEditVendorModal, setShowEditVendorModal] = useState();

  const { data: vendorData, refetch: refetchVendors } = useQuery(
    VENDORS_SELECT
  );

  const [connectToEntity] = useMutation(CONNECT_TXNS_TO_ENTITY, {
    onCompleted: () => {
      setSelected([]);
      setVendorId('');
      refetch();
    }
  });

  const handleConnect = () => {
    if (vendorId) {
      connectToEntity({
        variables: {
          entityType: 'Vendor',
          entityId: vendorId,
          txnIds: selected.map(txn => txn.id),
        }
      });
    } else {
      const editVendorId = selected
        .find(txn => 'Vendor' === txn.entityType)?.entityId;

      if (editVendorId) {
        const editVendor = vendorData.vendors.
          find(vendor => vendor.id === editVendorId)

        setShowEditVendorModal(editVendor);
      } else {
        setShowAddVendorModal(true);
      }
    }
  };

  const connectAndRefetch = data => {
    connectToEntity({
      variables: {
        entityType: 'Vendor',
        entityId: (data.createVendor || data.updateVendor).vendor.id,
        txnIds: selected.map(txn => txn.id),
      }
    });
  };

  const toggleTxnSelected = entry => {
    if (selected.find(txn => txn.id === entry.id)) {
      setSelected(selected.filter(txn => txn.id !== entry.id));
    } else {
      setSelected([...selected, entry]);
    }
  };

  const columns = [
    {
      key: 'id',
      label: '✓',
      sort: 'prevent',
      content: ({ entry }) => (
        <input
          type="checkbox"
          checked={!!selected.find(txn => txn.id === entry.id)}
          onChange={e => toggleTxnSelected(entry)}
        />
      )
    },
    {
      key: 'bankDate',
      label: 'Date',
      value: ({ entry }) => entry.bankDate,
      content: dateShort,
    },
    {
      key: 'name',
      label: 'Name',
      sort: 'asc',
    },
    {
      key: 'amount',
      label: 'Amount',
      content: ({ value }) => currency(value),
    },
    {
      key: 'account',
      label: 'Expense Account',
      value: ({ entry }) => entry.txnLines[1]?.account.name,
    },
    {
      key: 'actions',
      label: 'Actions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>
        </LinkContainer>
      )
    },
  ];

  const buttons = (
    <View style={{ flexDirection: 'row', gap: 12 }}>
      <Text>{selected.length} selected</Text>
      {vendorData && selected.length > 0 && (
        <View style={{ flexDirection: 'row' }}>
          <View>
            <Text>Connect to:</Text>
            <select
              value={vendorId}
              onChange={e => setVendorId(e.target.value)}
              style={{ maxWidth: 400, height: 20 }}>
            >
              <option value=""></option>
              {vendorData.vendors.map(vendor => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                  {!vendor.taxIdNumber && ' [MISSING W9 INFO]'}
                </option>
              ))}
            </select>
          </View>
        </View>
      )}
      {vendorData && selected.length > 0 && (
        <Button
          label={vendorId ? 'Connect' : 'Setup Vendor'}
          onPress={handleConnect}
          style={{ width: 180 }}
        />
      )}
    </View>
  );

  return (
    <MainContainer>
      <SubNav />

      <ContentContainer>
        <PageTitle>Expenses Missing Vendor Data</PageTitle>

        <DataTable.Gql
          setRefetch={setRefetch}
          query={TXNS_WITH_COUNT}
          queryName="txnsWithCount"
          queryVariables={{ scopes: ['expense', 'vendor_issues'] }}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          htmlTable
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Expense"
        >
          <EditExpense
            txn={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
          />
        </Modal>
      }

      {showAddVendorModal && (
        <Modal
          showModal
          setShowModal={setShowAddVendorModal}
          modalType="Setup Vendor"
        >
          <NewVendor
            defaultName={selected[0].name}
            setShowModal={setShowAddVendorModal}
            refetch={connectAndRefetch}
          />
        </Modal>
      )}
      {showEditVendorModal && (
        <Modal
          showModal
          setShowModal={setShowEditVendorModal}
          modalType="Edit Vendor"
          objectName={showEditVendorModal.name}
        >
          <EditVendor
            vendor={showEditVendorModal}
            setShowModal={setShowEditVendorModal}
            refetch={connectAndRefetch}
          />
        </Modal>
      )}
    </MainContainer>
  );
};

export default VendorIssues;
